/* eslint-disable react/display-name */
import React, { useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useIntl } from 'react-intl';
import { useFormattedDuration } from 'modules/notes/common/components/Timer';
import { getFormattedPercentage } from 'modules/Insights/utils';
import { roundedDuration } from './utils';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(0, 1),
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    position: 'relative',
    display: 'flex'
  },
  overTimeIcon: {
    top: 0,
    right: 0,
    position: 'absolute',
    width: theme.spacing(1),
    height: theme.spacing(1),
    border: '4px solid transparent',
    borderTopColor: 'rgba(133, 109, 219, 1)',
    borderRightColor: 'rgba(133, 109, 219, 1)'
  },
  div: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  percentage: {
    color: theme.palette.text.secondary
  }
}));

type BillableHoursCellProps = {
  billableDuration: number;
  scheduledDuration: number;
};

const BillableHoursCell: React.FC<BillableHoursCellProps> = ({
  billableDuration,
  scheduledDuration
}: BillableHoursCellProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const percentage = useMemo(() => {
    let billablePercentage = 0;
    if (scheduledDuration > 0)
      billablePercentage = getFormattedPercentage(
        billableDuration,
        scheduledDuration
      );
    else if (billableDuration !== 0 && scheduledDuration === 0) {
      billablePercentage = 100;
    }

    const NonBillablePercentage = 100 - billablePercentage;
    return {
      billablePercentage: billablePercentage || 0,
      NonBillablePercentage: NonBillablePercentage || 0
    };
  }, [billableDuration, scheduledDuration]);

  const formattedBillableDuration = useFormattedDuration({
    needsMask: true,
    duration: billableDuration ?? 0
  });
  const formattedScheduleDuration = useFormattedDuration({
    needsMask: true,
    duration: scheduledDuration ?? 0
  });

  const formatedDurationToDisplay = useCallback((formatedDuration: string) => {
    const [hours, minutes] = formatedDuration.split(' ');
    if (minutes === '0m' && hours !== '0h') {
      return hours;
    } else if (hours === '0h' && minutes !== '0m') return minutes;
    else return formatedDuration;
  }, []);

  const durationToDisplay = useMemo(() => {
    if (
      formatedDurationToDisplay(formattedBillableDuration) === '0m' &&
      formatedDurationToDisplay(formattedScheduleDuration) === '0m'
    )
      return '0m';
    const billableHours = formatedDurationToDisplay(formattedBillableDuration);
    const scheduleHours = formatedDurationToDisplay(formattedScheduleDuration);
    const billableDurationText = formatMessage(
      {
        id: 'insights.teamTable.billableDurationText'
      },
      { billableHours: billableHours, scheduleHours: scheduleHours }
    );
    return billableDurationText;
  }, [
    formatMessage,
    formatedDurationToDisplay,
    formattedBillableDuration,
    formattedScheduleDuration
  ]);

  return (
    <Box
      className={classes.container}
      sx={{
        background: `linear-gradient(90deg, #d2f9ea 0%, #d2f9ea ${percentage.billablePercentage}% ,#eeeeee ${percentage.billablePercentage}%,#eeeeee 100% )`
      }}
      data-testid={'billable-hours-cell-container'}
    >
      {roundedDuration(billableDuration) >
        roundedDuration(scheduledDuration) && (
        <div
          className={classes.overTimeIcon}
          data-testid={'billable-hours-cell-overtime'}
        />
      )}
      <div className={classes.div}>
        <Typography
          variant="caption"
          alignSelf="center"
          data-testid="duration-text"
        >
          {durationToDisplay}
        </Typography>
        <Typography
          variant="caption"
          alignSelf="center"
          className={classes.percentage}
          data-testid="percentage-text"
        >
          {`(${percentage.billablePercentage}%)`}
        </Typography>
      </div>
    </Box>
  );
};
export default React.memo(BillableHoursCell);
